<template>
  <donut-page-sections-two-column :title="title">
    <template #left-side>
      <div class="t dark-gray">
        <span>Set how many {{ pointsNoun }} each teammate can give each period.</span><br><br>
        <span>
          Unused {{ pointsNoun }} won't roll over, but you can change this setting any time. Changing the allowance renewal&#32;
          period will take effect starting with the next cycle.
        </span>
      </div>
      <points-slack-previews-channel-shoutout-sent-ephemeral class="m-tx2" />
      <div v-if="advancedAllowances" class="t m-tx1">
        <span class="gray">You can assign Member and Manager roles on the&#32;</span>
        <a v-bind="hrefOrClickHandlerForLink('/shoutouts/people')" class="link">People page</a>
        <span class="gray">&#32;and Admins on the&#32;</span>
        <a v-bind="hrefOrClickHandlerForLink('/points/permissions')" class="link">Permissions page</a>
        <span class="gray">.</span>
      </div>
    </template>
    <template #right-side>
      <div class="f-je f f-v f-ae f-gap-16">
        <div class="f f-ac f-gap-16">
          <div class="t bold">Allowance renewal period</div>
          <div class="w-x15">
            <donut-selector-single-static
              v-model="renewalPeriod"
              :options="renewalPeriodOptions"
              :key-map="{ label: 'name', value: 'value' }"
              form-name="shoutouts_program[renewal_period]"
              :paywall="renewalPeriodPaywall"
            />
          </div>
        </div>
        <template v-for="(role, i) in validRoles">
          <div v-if="shouldDisplayRole(role)" :key="i" class="f f-ac f-gap-16">
            <div class="t bold">{{ roleTitle(role) }}</div>
            <div class="w-x15">
              <donut-input-async-number
                :model-value="allowances[roleKey(role)]"
                :paywall="pointsAllowancePaywall"
                class="full-width"
                @submit="(val, onComplete) => onAllowanceSubmit(role, val, onComplete)"
              />
            </div>
          </div>
        </template>
        <donut-input-checkbox :model-value="advancedAllowances" label="Enable advanced allowances" @update:model-value="toggleAdvancedAllowances" />
      </div>
    </template>
  </donut-page-sections-two-column>
</template>

<script setup>
  import { ref, computed, inject } from 'vue';
  import { useStore } from 'vuex';
  import { capitalize } from 'lodash';

  const $i18n = inject('$i18n');
  const { state, getters, dispatch } = useStore();

  const pointsNoun = $i18n.t('shoutouts.point', { count: 2 });
  const title = `${capitalize(pointsNoun)} Allowance`;
  const program = computed(() => state.shoutoutsProgram.program);

  // Renewal Period
  const renewalPeriodOptions = [
    { name: 'Weekly', value: 'weekly', noun: 'week' },
    { name: 'Monthly', value: 'monthly', noun: 'month' },
  ];
  const renewalPeriod = computed({
    get() { return renewalPeriodOptions.find(({ value }) => value === program.value.renewal_period); },
    set({ value }) {
      dispatch('shoutoutsProgram/updateProgram', {
        params: { renewal_period: value },
        successToast: 'Successfully updated renewal period!',
        errorToast: 'Failed to update renewal period.',
      });
    },
  });
  const renewalPeriodPaywall = computed(() => ({
      data: program.value.upgrade_data,
      feature: 'renewal_period',
      featureTitleText: 'set allowance renewal period',
      source: 'shoutouts-settings-renewal-period',
      programKey: 'shoutouts',
    }));

  // Allowances
  const pointsAllowancePaywall = computed(() => ({
      data: program.value.upgrade_data,
      feature: 'member_allowance',
      featureTitleText: 'set a custom allowance',
      source: 'shoutouts-settings-point-allowance',
      programKey: 'shoutouts',
    }));
  const validRoles = ['member', 'manager', 'admin'];
  const advancedRoles = validRoles.filter(r => r !== 'member');
  const roleKey = (role) => `${role}_allowance`;
  const allowances = ref(Object.fromEntries(validRoles.map(r => [roleKey(r), program.value[roleKey(r)]])));
  const advancedAllowances = ref(program.value.advanced_allowances);
  const shouldDisplayRole = (role) => role === 'member' || advancedAllowances.value;
  const roleTitle = (role) => (advancedAllowances.value ? `${capitalize(role)} Allowance` : 'Allowance');
  const toggleAdvancedAllowances = () => {
    // If we're toggling on, we display the advanced allowances and set them to be the value of the member allowance (for now).
    // If we're toggling off, we don't display them and set them to be null.
    const togglingOn = !advancedAllowances.value;
    advancedRoles.forEach((role) => {
      allowances.value[roleKey(role)] = togglingOn ? allowances.value.member_allowance : null;
    });
    advancedAllowances.value = togglingOn;
    if (!getters.upgradeRequired(pointsAllowancePaywall.value)) {
      dispatch('shoutoutsProgram/updateProgram', {
        params: allowances.value,
        successToast: 'Successfully enabled advanced allowances!',
        errorToast: 'Failed to enable advanced allowances.',
      });
    }
  };
  const onAllowanceSubmit = (role, val, onComplete) => {
    const currentValue = allowances.value[roleKey(role)];
    // First, catch for invalid values (non-integers, integers less than 0)
    if (val < 0 || parseInt(val) !== val) {
      onComplete({ icon: 'error', value: currentValue });
      return;
    }
    // Once past that, submit the whole allowances object to the controller. We have to pass the whole thing
    // because the controller validates all of them at once so passing for instance, a null value just to the
    // admin_allowance without the manager_allowance as well, will raise.
    allowances.value[roleKey(role)] = val;
    dispatch('shoutoutsProgram/updateProgram', {
        params: allowances.value,
        successToast: 'Successfully updated allowance!',
        errorToast: 'Failed to update allowance.',
      })
        .then(() => onComplete({ icon: 'success' }))
        .catch(() => onComplete({ icon: 'error', value: currentValue }));
  };

  // Links
  const upgradeRequired = (feature) => getters.upgradeRequired({ data: state.shoutoutsProgram.program.upgrade_data, feature });
  const hrefOrClickHandlerForLink = (url) => {
    // The two links in the copy should be paywalled. Make them open the upgrade modal if need be, else they're just normal hrefs.
    const feature = 'people_permissions_links';
    if (upgradeRequired(feature)) {
      const paywall = {
        data: program.value.upgrade_data,
        feature,
        featureTitleText: 'view this page',
        source: 'shoutouts-settings-people-permissions-links',
        programKey: 'shoutouts',
      };
      return { onClick: () => dispatch('openUpgradeModalIfRequired', paywall) };
    }
      return { href: url };
  };
</script>
